import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  archiveNotificationsApi,
  deleteNotificationsApi,
  getNotificationSettingsApi,
  getNotificationsApi,
  getUnreadCountApi,
  markNotificationsReadApi,
  updateNotificationSettingsApi,
} from '~/api/notifications';
import type { Notification, NotificationSettings } from '~/types/notifications';

const PLATFORM = 'ai';

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([]);
  const unreadCount = ref(0);
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const hasMore = ref(false);
  const pagination = ref({
    page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 0,
  });
  const settings = ref<NotificationSettings | null>(null);
  const isContentModalOpen = ref(false);
  const activeNotification = ref<Notification | null>(null);

  const chunkArray = <T>(items: T[], size: number): T[][] => {
    if (size <= 0) return [items];
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  };

  const fetchAllNotificationUuids = async (): Promise<string[]> => {
    const uuids = new Set<string>();
    const pageSize = 100;
    let page = 1;
    let totalPages = 1;

    do {
      const response = await getNotificationsApi({
        platform: PLATFORM,
        page,
        page_size: pageSize,
      });
      for (const notification of response.notifications) {
        uuids.add(notification.uuid);
      }
      totalPages = response.pagination.total_pages;
      page += 1;
    } while (page <= totalPages);

    return [...uuids];
  };

  /**
   * 獲取通知列表
   */
  const fetchNotifications = async (params?: {
    type?: string;
    is_read?: boolean;
    is_archived?: boolean;
    page?: number;
    page_size?: number;
    append?: boolean; // 是否追加到現有列表
  }) => {
    try {
      const isFirstPage = !params?.append && (!params?.page || params.page === 1);

      if (isFirstPage) {
        isLoading.value = true;
      } else {
        isLoadingMore.value = true;
      }

      const response = await getNotificationsApi({
        platform: PLATFORM,
        ...params,
      });

      if (params?.append) {
        // 追加模式：將新通知追加到現有列表
        notifications.value.push(...response.notifications);
      } else {
        // 替換模式：替換整個列表
        notifications.value = response.notifications;
      }

      pagination.value = response.pagination;
      // 判斷是否還有更多資料
      hasMore.value = response.pagination.page < response.pagination.total_pages;

      return response;
    } catch (error) {
      console.error('獲取通知列表失敗:', error);
      throw error;
    } finally {
      isLoading.value = false;
      isLoadingMore.value = false;
    }
  };

  /**
   * 載入更多通知（無限滾動）
   */
  const loadMoreNotifications = async () => {
    if (isLoadingMore.value || !hasMore.value) {
      return;
    }

    const nextPage = pagination.value.page + 1;
    await fetchNotifications({
      page: nextPage,
      page_size: pagination.value.page_size,
      append: true,
    });
  };

  /**
   * 獲取未讀通知數量
   */
  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadCountApi(PLATFORM);
      unreadCount.value = response.unread_count;
      return response.unread_count;
    } catch (error) {
      console.error('獲取未讀數量失敗:', error);
      throw error;
    }
  };

  /**
   * 標記通知為已讀
   */
  const markAsRead = async (uuids: string[]) => {
    // 儲存原始狀態，以便失敗時回滾
    const originalStates = notifications.value
      .filter((n) => uuids.includes(n.uuid))
      .map((n) => ({
        uuid: n.uuid,
        is_read: n.is_read,
        read_at: n.read_at,
      }));

    // Optimistic update: 先更新本地狀態
    for (const notification of notifications.value) {
      if (uuids.includes(notification.uuid)) {
        notification.is_read = true;
        notification.read_at = Date.now();
      }
    }

    // 更新未讀數量（optimistic）
    const affectedCount = originalStates.filter((s) => !s.is_read).length;
    unreadCount.value = Math.max(0, unreadCount.value - affectedCount);

    try {
      const response = await markNotificationsReadApi({ uuids });
      // API 成功後，重新獲取未讀數量以確保同步
      // await fetchUnreadCount();
      return response;
    } catch (error) {
      console.error('標記已讀失敗:', error);
      // 回滾狀態
      for (const originalState of originalStates) {
        const notification = notifications.value.find((n) => n.uuid === originalState.uuid);
        if (notification) {
          notification.is_read = originalState.is_read;
          notification.read_at = originalState.read_at;
        }
      }
      // 回滾未讀數量
      await fetchUnreadCount();
      throw error;
    }
  };

  /**
   * 標記所有通知為已讀
   */
  const markAllAsRead = async () => {
    const unreadUuids = notifications.value.filter((n) => !n.is_read).map((n) => n.uuid);
    if (unreadUuids.length > 0) {
      await markAsRead(unreadUuids);
    }
  };

  /**
   * 刪除通知
   */
  const deleteNotifications = async (uuids: string[]) => {
    // 儲存被刪除的通知，以便失敗時回滾
    const deletedNotifications = notifications.value.filter((n) => uuids.includes(n.uuid));

    // 計算被刪除的未讀通知數量
    const deletedUnreadCount = deletedNotifications.filter((n) => !n.is_read).length;

    // Optimistic update: 先從本地狀態中移除
    notifications.value = notifications.value.filter(
      (notification) => !uuids.includes(notification.uuid)
    );

    // 更新未讀數量（optimistic）
    unreadCount.value = Math.max(0, unreadCount.value - deletedUnreadCount);

    try {
      const response = await deleteNotificationsApi({ uuids });
      // API 成功後，重新獲取未讀數量以確保同步
      await fetchUnreadCount();
      return response;
    } catch (error) {
      console.error('刪除通知失敗:', error);
      // 回滾狀態：將被刪除的通知加回列表
      notifications.value.push(...deletedNotifications);
      // 重新排序以保持原有順序（如果需要）
      notifications.value.sort((a, b) => b.created_at - a.created_at);
      // 回滾未讀數量
      await fetchUnreadCount();
      throw error;
    }
  };

  /**
   * 刪除所有通知
   */
  const deleteAllNotifications = async () => {
    // 儲存所有通知與狀態，以便失敗時回滾
    const allNotifications = [...notifications.value];
    const originalUnreadCount = unreadCount.value;
    const originalPagination = { ...pagination.value };
    const originalHasMore = hasMore.value;

    // Optimistic update: 先清空列表（UI 立即回應）
    notifications.value = [];
    unreadCount.value = 0;
    hasMore.value = false;
    pagination.value = {
      ...pagination.value,
      page: 1,
      total_count: 0,
      total_pages: 0,
    };

    try {
      const allUuids = await fetchAllNotificationUuids();
      if (allUuids.length === 0) {
        // 伺服器已無通知，未讀數已由 optimistic update 設為 0，僅嘗試同步
        try {
          await fetchUnreadCount();
        } catch (e) {
          console.error('獲取未讀數量失敗:', e);
        }
        return {
          success_count: 0,
          failed_count: 0,
          failed_uuids: [],
        };
      }

      let successCount = 0;
      let failedCount = 0;
      const failedUuids: string[] = [];
      const batches = chunkArray(allUuids, 100);

      for (const batch of batches) {
        const response = await deleteNotificationsApi({ uuids: batch });
        successCount += response?.success_count ?? 0;
        failedCount += response?.failed_count ?? 0;
        const batchFailed = Array.isArray(response?.failed_uuids) ? response.failed_uuids : [];
        failedUuids.push(...batchFailed);
      }

      // 刪除已成功，未讀數已由 optimistic update 設為 0；僅嘗試與伺服器同步，失敗不影響 UI
      try {
        await fetchUnreadCount();
      } catch (e) {
        console.error('獲取未讀數量失敗:', e);
      }
      return {
        success_count: successCount,
        failed_count: failedCount,
        failed_uuids: failedUuids,
      };
    } catch (error) {
      console.error('刪除所有通知失敗:', error);
      // 回滾狀態：恢復所有通知與未讀數
      notifications.value = allNotifications;
      unreadCount.value = originalUnreadCount;
      pagination.value = originalPagination;
      hasMore.value = originalHasMore;
      try {
        await fetchUnreadCount();
      } catch (e) {
        console.error('獲取未讀數量失敗:', e);
      }
      throw error;
    }
  };

  /**
   * 歸檔通知
   */
  const archiveNotifications = async (uuids: string[]) => {
    try {
      const response = await archiveNotificationsApi({ uuids });
      // 更新本地狀態
      for (const notification of notifications.value) {
        if (uuids.includes(notification.uuid)) {
          notification.is_archived = true;
          notification.archived_at = Date.now();
        }
      }
      return response;
    } catch (error) {
      console.error('歸檔通知失敗:', error);
      throw error;
    }
  };

  /**
   * 獲取通知設置
   */
  const fetchSettings = async () => {
    try {
      const response = await getNotificationSettingsApi(PLATFORM);
      settings.value = response;
      return response;
    } catch (error) {
      console.error('獲取通知設置失敗:', error);
      throw error;
    }
  };

  /**
   * 更新通知設置
   */
  const updateSettings = async (enabledTypes: string[], disabledTypes: string[]) => {
    try {
      const response = await updateNotificationSettingsApi({
        platform: PLATFORM,
        enabled_types: enabledTypes,
        disabled_types: disabledTypes,
      });
      settings.value = response;
      return response;
    } catch (error) {
      console.error('更新通知設置失敗:', error);
      throw error;
    }
  };

  /**
   * 計算屬性：未讀通知列表
   */
  const unreadNotifications = computed(() => notifications.value.filter((n) => !n.is_read));

  /**
   * 計算屬性：已讀通知列表
   */
  const readNotifications = computed(() => notifications.value.filter((n) => n.is_read));

  /**
   * 重置狀態
   */
  const reset = () => {
    notifications.value = [];
    unreadCount.value = 0;
    isLoading.value = false;
    isLoadingMore.value = false;
    hasMore.value = false;
    pagination.value = {
      page: 1,
      page_size: 20,
      total_count: 0,
      total_pages: 0,
    };
    settings.value = null;
    isContentModalOpen.value = false;
    activeNotification.value = null;
  };

  /**
   * 開啟通知內容視窗
   */
  const openContentModal = (notification: Notification) => {
    activeNotification.value = notification;
    isContentModalOpen.value = true;
  };

  /**
   * 關閉通知內容視窗
   */
  const closeContentModal = () => {
    isContentModalOpen.value = false;
    // activeNotification.value = null;
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    isLoadingMore,
    hasMore,
    pagination,
    settings,
    isContentModalOpen,
    activeNotification,
    unreadNotifications,
    readNotifications,
    fetchNotifications,
    loadMoreNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotifications,
    deleteAllNotifications,
    archiveNotifications,
    fetchSettings,
    updateSettings,
    openContentModal,
    closeContentModal,
    reset,
  };
});
